import React from 'react'

import {
  Table,
  Tbody,
  Tr,
  Td,
  Tag,
  Text,
  Wrap,
  Tooltip,
  HStack,
  Icon,
  TagLabel,
  Box,
  WrapItem,
} from '@chakra-ui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useSelector } from 'react-redux'

import Attachments from './Attachments'
import CaseworkStatus from './CaseworkStatus'
import HighlightedText from './HighlightedText'
import NYCHAModal from './NYCHAModal'
import {
  MARK_STYLES,
  hasHighlight as checkHighlight,
} from '../../../constants/highlightConfig'
import { priorityColorSchemes } from '../../../constants/statuses'
import {
  downloadFile,
  convertDistrictToNumeric,
  sanitizeHighlight,
} from '../../../utils'
import { AgencyButton, ContactInfoBox } from '../../directory'
import {
  AddressText,
  FormattedDate,
  NoValue,
  Person,
  ShowHideFormField,
} from '../../ui'
import { renderTextLines } from '../../utils'

const TableRow = ({ heading, children, verticalAlign = 'center', pt }) => (
  <Tr>
    <Td w='15%' px={0} verticalAlign={verticalAlign} pt={pt}>
      <Text as='b'> {heading.toUpperCase()} </Text>
    </Td>
    <Td verticalAlign={verticalAlign} px={2} my='auto'>
      {children || <NoValue />}
    </Td>
  </Tr>
)

const renderTextWithHighlight = (
  text,
  highlightHtml,
  onNYCHAClick,
  enableNYCHA
) => {
  if (highlightHtml) {
    return <HighlightedText html={highlightHtml} maxLength={10000} />
  }
  return renderTextLines(text, onNYCHAClick, enableNYCHA)
}

const CaseworkDetailTable = ({
  tableVariant = 'simple',
  status,
  constituent,
  agencies,
  address,
  district,
  topics,
  tags,
  details,
  priority,
  last_updated,
  created_at,
  opened_at,
  closed_at,
  comments,
  contact_methods,
  referral_staff,
  reference_numbers,
  attachments,
  detailView = false, // show for detail page only
  hideConstituent,
  filtered = false, //show certain values if filter is active
  details_headline, // highlighted version from search
  comments_headline, // highlighted version from search
  reference_numbers_headline, // highlighted version from search
  constituent_headline, // highlighted version from search
  address_headline, // highlighted version from search
  topics_headline, // highlighted version from search
}) => {
  const user = useSelector(state => state.auth.user)
  const filters = useSelector(state => state.casework.filters)

  const [selectedNYCHANumber, setSelectedNYCHANumber] = React.useState(null)

  const hasNYCHATopic = React.useMemo(() => {
    return topics?.some(
      topic =>
        topic.name?.toLowerCase().includes('nycha') ||
        topic.issue?.toLowerCase().includes('nycha')
    )
  }, [topics])

  const handleNYCHAClick = workOrderNumber => {
    setSelectedNYCHANumber(workOrderNumber)
  }

  const handleNYCHAClose = () => {
    setSelectedNYCHANumber(null)
  }

  const getTopicHighlight = topicName => {
    if (!topics_headline) return null

    const highlightedSegments = topics_headline.split(', ')
    const matchedSegment = highlightedSegments.find(segment => {
      const cleanSegment = segment.replace(/<\/?mark>/g, '').trim()
      return cleanSegment === topicName.trim()
    })

    return matchedSegment || null
  }

  return (
    <>
      <Table variant={tableVariant}>
        <Tbody>
          <TableRow heading='Status'>
            <CaseworkStatus status={status} />
          </TableRow>
          {!hideConstituent && (
            <TableRow heading='Constituent' verticalAlign='top' pt={6}>
              {constituent && (
                <>
                  <ShowHideFormField
                    heading={
                      <Person
                        link={`/directory/constituents/${constituent.id}`}
                        name={constituent.name}
                        highlightedName={constituent_headline}
                      />
                    }
                  >
                    <ContactInfoBox {...constituent} />
                  </ShowHideFormField>
                </>
              )}
            </TableRow>
          )}
          {user?.is_admin && (
            <TableRow heading='District'>
              {convertDistrictToNumeric(district)}
            </TableRow>
          )}
          <TableRow heading='Topic'>
            {topics?.length > 0 ? (
              <Wrap>
                {topics.map((topic, index) => {
                  const highlightedName = getTopicHighlight(topic.name)
                  const hasHighlight = checkHighlight(highlightedName)

                  return (
                    <Tooltip key={index} label={topic.issue}>
                      <Tag variant='outline' p={2} sx={MARK_STYLES}>
                        {hasHighlight ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHighlight(highlightedName),
                            }}
                          />
                        ) : (
                          topic.name
                        )}
                      </Tag>
                    </Tooltip>
                  )
                })}
              </Wrap>
            ) : (
              <HStack>
                <Icon as={ExclamationTriangleIcon} color='yellow.500' />
                <Text color='yellow.500'>Unassigned</Text>
              </HStack>
            )}
          </TableRow>
          <TableRow heading='Details' verticalAlign='top'>
            <Box maxH={350} overflowY='auto' pr={2}>
              {renderTextWithHighlight(
                details,
                details_headline,
                handleNYCHAClick,
                hasNYCHATopic
              )}
            </Box>
          </TableRow>

          {(detailView || comments) && (
            <TableRow heading='Comments' verticalAlign='top'>
              {comments ? (
                <Box maxH={200} overflowY='auto' pr={2}>
                  {renderTextWithHighlight(
                    comments,
                    comments_headline,
                    handleNYCHAClick,
                    hasNYCHATopic
                  )}
                </Box>
              ) : (
                <NoValue />
              )}
            </TableRow>
          )}

          {detailView && (
            <>
              {
                <TableRow heading='Priority'>
                  <Tag
                    colorScheme={priorityColorSchemes[priority]}
                    borderRadius='md'
                    textAlign='center'
                    textTransform='uppercase'
                  >
                    {priority}
                  </Tag>
                </TableRow>
              }
              <TableRow heading='Report Address'>
                {address_headline ? (
                  <HighlightedText html={address_headline} maxLength={200} />
                ) : (
                  <AddressText address={address} />
                )}
              </TableRow>
              <TableRow heading='Last Updated'>
                <FormattedDate date={last_updated} />
              </TableRow>
              {status === 'REQUESTED' && (
                <TableRow heading='Submitted'>
                  <FormattedDate date={created_at} />
                </TableRow>
              )}
              <TableRow heading='Opened'>
                <FormattedDate date={opened_at} />
              </TableRow>
              <TableRow heading='Closed'>
                <FormattedDate date={closed_at} />
              </TableRow>
            </>
          )}
          {(detailView || (filtered && filters['agencies'])) && (
            <TableRow heading='City Agencies'>
              {agencies.length > 0 ? (
                <Wrap>
                  {agencies.map((agency, index) => (
                    <WrapItem key={index}>
                      <AgencyButton agencyName={agency} />
                    </WrapItem>
                  ))}
                </Wrap>
              ) : (
                <NoValue />
              )}
            </TableRow>
          )}
          {(detailView || (filtered && filters['tags'])) && (
            <TableRow heading='Tags'>
              <Wrap>
                {tags?.length > 0 ? (
                  tags.map((tag, index) => (
                    <Tag key={index}>
                      <TagLabel>{tag.label}</TagLabel>
                    </Tag>
                  ))
                ) : (
                  <NoValue />
                )}
              </Wrap>
            </TableRow>
          )}
          {detailView && (
            <TableRow heading='Contact Method'>
              {contact_methods?.length ? (
                contact_methods.map(cm => cm.name).join(', ')
              ) : (
                <NoValue />
              )}
            </TableRow>
          )}

          {detailView && (
            <TableRow heading='Referral By'>
              {referral_staff ? (
                <Person
                  link={`/directory/staff/${referral_staff.id}`}
                  name={referral_staff.full_name}
                />
              ) : (
                <NoValue />
              )}
            </TableRow>
          )}
          {detailView && (
            <TableRow heading='Reference Numbers' verticalAlign='top'>
              {reference_numbers ? (
                <Box maxH={200} overflowY='auto'>
                  {renderTextWithHighlight(
                    reference_numbers,
                    reference_numbers_headline,
                    handleNYCHAClick,
                    hasNYCHATopic
                  )}
                </Box>
              ) : (
                <NoValue />
              )}
            </TableRow>
          )}
          {detailView && (
            <TableRow heading='Attachments'>
              {attachments?.length > 0 ? (
                <Attachments
                  attachments={attachments}
                  onOpenAttachment={file => {
                    downloadFile(file.file_url, file.file_name)
                  }}
                />
              ) : (
                <NoValue />
              )}
            </TableRow>
          )}
        </Tbody>
      </Table>
      {selectedNYCHANumber && (
        <NYCHAModal
          isOpen={!!selectedNYCHANumber}
          onClose={handleNYCHAClose}
          workOrderNumber={selectedNYCHANumber}
        />
      )}
    </>
  )
}

export default CaseworkDetailTable
